import { useSelector } from "react-redux";
import { useCustomRoles } from "./useCustomRoles";
import { canCreateTeamMembers } from "../../utils/rolesPermissions";

export const useTeamValidation = () => {
  const { customRoles, hasRoles } = useCustomRoles();
  const { isMainVendor, userPermissions } = useSelector((state) => state.user);
  const teamMembers = useSelector((state) => state.team.teamMembers);

  //Todo => Check if can create team members
  const canCreateTeamMembersValidation = () => {
    if (!canCreateTeamMembers(userPermissions, isMainVendor)) {
      return {
        canCreate: false,
        reason: "You don't have permission to create team members",
      };
    }

    if (!hasRoles) {
      return {
        canCreate: false,
        reason: "You must create at least one role before adding team members",
      };
    }

    return {
      canCreate: true,
      reason: null,
    };
  };

  //Todo => Check if role can be deleted
  const canDeleteRole = (roleId) => {
    let role = customRoles.find((r) => r.id === roleId);
    if (!role) {
      role = customRoles.find((r) => r.role_slug === roleId);
    }

    if (!role) {
      console.log(
        "Role not found for ID/slug:",
        roleId,
        "Available roles:",
        customRoles
      );
      return {
        canDelete: false,
        reason: "Role not found",
        affectedMembers: [],
      };
    }

    const membersWithRole = teamMembers.filter(
      (member) => member.role === role.name
    );
    console.log(`Checking role "${role.name}" for team members:`, {
      roleToCheck: role.name,
      teamMembers: teamMembers,
      membersWithRole: membersWithRole,
    });

    if (membersWithRole.length > 0) {
      return {
        canDelete: false,
        reason: `Cannot delete role. ${membersWithRole.length} team member(s) are assigned to this role.`,
        affectedMembers: membersWithRole,
      };
    }

    return {
      canDelete: true,
      reason: null,
      affectedMembers: [],
    };
  };

  //Todo => Validate team member data
  const validateTeamMemberData = (memberData) => {
    const errors = {};

    //Todo => Basic validation
    if (!memberData.fullName?.trim()) {
      errors.fullName = "Full name is required";
    }

    if (!memberData.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(memberData.email)) {
      errors.email = "Invalid email format";
    }

    //Todo => Check for duplicate email (exclude current member)
    const existingMember = teamMembers.find(
      (member) =>
        member.email.toLowerCase() === memberData.email.toLowerCase() &&
        member.id !== memberData.id
    );
    if (existingMember) {
      errors.email = "Email already exists for another team member";
    }

    //Todo => Role validation - support both role name and role_slug
    if (!memberData.role?.trim() && !memberData.role_slug?.trim()) {
      errors.role = "Role is required";
    } else if (memberData.role?.trim()) {
      const roleExists = customRoles.some(
        (role) => role.name === memberData.role
      );
      if (!roleExists) {
        errors.role = "Selected role does not exist";
      }
    } else if (memberData.role_slug?.trim()) {
      const roleExists = customRoles.some(
        (role) => role.role_slug === memberData.role_slug
      );
      if (!roleExists) {
        errors.role = "Selected role does not exist";
      }
    }

    //Todo => Vendor slug validation (only for edit operations, not create)
    if (memberData.id && !memberData.vendorSlug?.trim()) {
      errors.vendorSlug = "Vendor information is required";
    }

    //Todo => Phone validation (optional)
    if (
      memberData.phone?.trim() &&
      !/^\+?[\d\s\-()]+$/.test(memberData.phone)
    ) {
      errors.phone = "Invalid phone number format";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  //Todo => Get role suggestions based on permissions needed
  const suggestRolesForPermissions = (neededPermissions = []) => {
    return customRoles.filter((role) => {
      return neededPermissions.every((permission) => {
        return Object.values(role.permissions).flat().includes(permission);
      });
    });
  };

  //Todo => Check if role edit will affect team members
  const checkRoleEditImpact = (roleId, newPermissions) => {
    const role = customRoles.find((r) => r.id === roleId);
    if (!role) return { affectedMembers: [], changes: [] };

    const affectedMembers = teamMembers.filter(
      (member) => member.role === role.name
    );

    const changes = [];
    Object.keys(role.permissions).forEach((module) => {
      const oldPermissions = role.permissions[module] || [];
      const newModulePermissions = newPermissions[module] || [];

      const removedPermissions = oldPermissions.filter(
        (perm) => !newModulePermissions.includes(perm)
      );

      if (removedPermissions.length > 0) {
        changes.push({
          type: "removed",
          module,
          permissions: removedPermissions,
        });
      }
    });

    Object.keys(newPermissions).forEach((module) => {
      const oldPermissions = role.permissions[module] || [];
      const newModulePermissions = newPermissions[module] || [];

      const addedPermissions = newModulePermissions.filter(
        (perm) => !oldPermissions.includes(perm)
      );

      if (addedPermissions.length > 0) {
        changes.push({
          type: "added",
          module,
          permissions: addedPermissions,
        });
      }
    });

    return {
      affectedMembers,
      changes,
      hasChanges: changes.length > 0,
    };
  };

  return {
    canCreateTeamMembers: canCreateTeamMembersValidation,
    canDeleteRole,
    validateTeamMemberData,
    suggestRolesForPermissions,
    checkRoleEditImpact,
    hasRoles,
    totalRoles: customRoles.length,
    totalMembers: teamMembers.length,
    isMainVendor,
  };
};
